/**
 * Utility functions for formatting values
 */

/**
 * Format a number as currency with Naira symbol
 * @param value - The numeric value to format
 * @param currency - Currency symbol (default: ₦)
 * @param decimals - Number of decimal places (default: 2)
 */
export function formatCurrency(value: number | string | null | undefined, currency: string = "₦", decimals: number = 2): string {
  if (value === null || value === undefined) return "-";
  
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  
  if (isNaN(numValue)) return "-";
  
  return `${currency}${numValue.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

/**
 * Format a number with commas (no currency symbol)
 * @param value - The numeric value to format
 * @param decimals - Number of decimal places (default: 0 for integers, 2 for floats)
 */
export function formatNumber(value: number | string | null | undefined, decimals?: number): string {
  if (value === null || value === undefined) return "-";
  
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  
  if (isNaN(numValue)) return "-";
  
  // Auto-detect decimals if not specified
  const finalDecimals = decimals !== undefined 
    ? decimals 
    : (numValue % 1 === 0 ? 0 : 2);
  
  return numValue.toLocaleString("en-US", {
    minimumFractionDigits: finalDecimals,
    maximumFractionDigits: finalDecimals,
  });
}

/**
 * Format a compact number (K, M, B notation)
 * @param value - The numeric value to format
 */
export function formatCompactNumber(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return "-";
  
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  
  if (isNaN(numValue)) return "-";
  
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
  }).format(numValue);
}

/**
 * Detect if a column name/value represents currency/revenue
 */
export function isCurrencyColumn(columnName: string): boolean {
  const currencyKeywords = [
    "revenue", "price", "cost", "amount", "total", 
    "sales", "value", "naira", "ngn"
  ];
  
  const lowerName = columnName.toLowerCase();
  return currencyKeywords.some(keyword => lowerName.includes(keyword));
}

/**
 * Smart format - detect currency columns and format appropriately
 */
export function smartFormat(value: any, columnName?: string): string {
  if (value === null || value === undefined) return "-";
  
  // Handle booleans
  if (typeof value === "boolean") return value ? "Yes" : "No";
  
  // Handle numbers
  if (typeof value === "number") {
    // Check if it's a currency column
    if (columnName && isCurrencyColumn(columnName)) {
      return formatCurrency(value);
    }
    // Regular number formatting
    return formatNumber(value);
  }
  
  // Everything else as string
  return String(value);
}
